import React, { useState, useMemo } from "react";
import { DateTimePicker, DateTimePickerProps } from "@mantine/dates";
import dayjs from "dayjs";

import styles from "@scss/custom-components/form-fields-dropdowns.module.scss";
import CustomTimePopover from "./custom-time-popover";

type CustomDateTimePickerProps = DateTimePickerProps;

export const CustomDateTimePickerInput: React.FC<CustomDateTimePickerProps> = ({
  variant = "default",
  classNames = {},
  ...props
}) => {
  const [internalValue, setInternalValue] = useState<Date | null>(null);

  // normalize incoming prop value (accept string or Date)
  const rawPropValue = (props as any).value;
  const propValue =
    rawPropValue === undefined
      ? undefined
      : typeof rawPropValue === "string"
        ? (() => {
            const parsed = dayjs(rawPropValue, "YYYY-MM-DD HH:mm:ss", true);
            return parsed.isValid() ? parsed.toDate() : null;
          })()
        : rawPropValue instanceof Date
          ? rawPropValue
          : null;

  const propOnChange = (
    props as DateTimePickerProps & {
      onChange?: (d: Date | null | string) => void;
    }
  ).onChange;

  const effectiveValue =
    propValue === undefined ? internalValue : (propValue ?? null);

  const effectiveOnChange =
    propOnChange ??
    ((v: Date | null | string) => {
      // keep previous uncontrolled behavior
      if (typeof v === "string") {
        const parsed = dayjs(v, "YYYY-MM-DD HH:mm:ss", true);
        setInternalValue(parsed.isValid() ? parsed.toDate() : null);
      } else {
        setInternalValue(v as Date | null);
      }
    });

  const variantDropdownClasses: Record<string, string> = {
    default: styles["dropdown--default"],
    dark: styles["dropdown--dark"],
    darker: styles["dropdown--darker"],
    sky: styles["dropdown--sky"],
  };

  const customDropdownClass = variantDropdownClasses[variant] || "";

  const timePickerControl = useMemo(
    () => (
      <CustomTimePopover
        value={effectiveValue}
        onChange={(val: Date | null) => {
          // CustomTimePopover expected to call with Date | null; format and forward
          effectiveOnChange(
            val ? dayjs(val).format("YYYY-MM-DD HH:mm:ss") : null,
          );
        }}
        variant={variant}
        withinPortal={false}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [effectiveValue, variant],
  );

  const formatForMantine = (d: Date | null) =>
    d ? dayjs(d).format("YYYY-MM-DD HH:mm:ss") : null;

  const handleDateTimeChange = (v: any) => {
    if (v == null) {
      effectiveOnChange(null);
      return;
    }

    let parsedDate: Date | null = null;

    if (v instanceof Date) {
      parsedDate = v;
    } else if (typeof v === "string") {
      const p = dayjs(v, "YYYY-MM-DD HH:mm:ss", true);
      parsedDate = p.isValid() ? p.toDate() : null;
    } else {
      try {
        parsedDate = v as Date;
      } catch {
        parsedDate = null;
      }
    }

    effectiveOnChange(
      parsedDate ? dayjs(parsedDate).format("YYYY-MM-DD HH:mm:ss") : null,
    );
  };

  const formatTimeForPicker = (d: Date | null) =>
    d ? dayjs(d).format("HH:mm:ss") : "";

  const handleTimePickerChange = (time: string) => {
    if (!time) {
      effectiveOnChange(null);
      return;
    }
    const parts = time.split(":").map((p) => parseInt(p, 10));
    const hh = Number.isFinite(parts[0]) ? parts[0] : 0;
    const mm = Number.isFinite(parts[1]) ? parts[1] : 0;
    const ss = Number.isFinite(parts[2]) ? parts[2] : 0;
    const next = effectiveValue ? new Date(effectiveValue) : new Date();
    next.setHours(hh, mm, ss, 0);
    effectiveOnChange(dayjs(next).format("YYYY-MM-DD HH:mm:ss"));
  };

  return (
    <DateTimePicker
      variant={variant}
      classNames={{
        ...classNames,
        calendarHeader: styles.calendarHeader,
        calendarHeaderControl: styles.calendarHeaderControl,
        calendarHeaderLevel: styles.calendarHeaderLevel,
        weekday: styles.weekday,
        day: styles.day,
        monthsListControl: styles.month,
        yearsListControl: styles.year,
        timeInput: styles.timeInput,
        submitButton: styles.submitButton,
      }}
      popoverProps={{
        ...(props as any)?.popoverProps,
        // render DateTimePicker dropdown inside the current DOM (no portal)
        withinPortal: false,
        classNames: {
          ...(props as any)?.popoverProps?.classNames,
          dropdown: customDropdownClass,
        },
      }}
      {...props}
      timePickerProps={
        {
          ...(props as any).timePickerProps,
          popoverProps: {
            ...(props as any)?.timePickerProps?.popoverProps,
            withinPortal: false,
          },
          rightSection:
            (props as any)?.timePickerProps?.rightSection ?? timePickerControl,
          value:
            (props as any)?.timePickerProps?.value ??
            formatTimeForPicker(effectiveValue),
          onChange:
            (props as any)?.timePickerProps?.onChange ?? handleTimePickerChange,
        } as any
      }
      value={formatForMantine(effectiveValue) as any}
      onChange={handleDateTimeChange as any}
    />
  );
};
